import React, { useState, useEffect, useRef } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Send,
  Paperclip,
  Smile,
  MoreVertical,
  User,
  Clock,
  CheckCircle,
  AlertCircle,
  Phone,
  Video,
  Archive,
  X,
  Bot,
  XCircle,
  ArrowLeft
} from 'lucide-react'
import { useChatStore } from '../stores/chatStore'
import { useAuthStore } from '../stores/authStore'
import { socketService } from '../services/socketService'
import { format } from 'date-fns'
import toast from 'react-hot-toast'

const ChatInterface = () => {
  const { sessionId: routeSessionId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  // /chat?session=<id> (email link) and /chat/<id> both open that exact chat
  const sessionId = routeSessionId || searchParams.get('session')
  const {
    sessions,
    activeSessionId,
    messages,
    setActiveSession,
    sendMessage,
    closeChat,
    fetchSessions
  } = useChatStore()
  const activeSession = sessions.find(s => s.sessionId === activeSessionId) || null
  
  const [messageText, setMessageText] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [activeTab, setActiveTab] = useState('active') // 'ai', 'active', 'closed'
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  // Helper function to safely format dates
  const formatDate = (timestamp, fallback = 'Just now') => {
    if (!timestamp) return fallback;
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? fallback : format(date, 'h:mm a');
  }

  function formatToIndianDateTime(isoString) {
  if (!isoString) return "";

  return new Date(isoString).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true
  });
}


  // Auto-switch to active tab if there are active sessions and no AI sessions
  useEffect(() => {
    if (sessions.length > 0) {
      const hasAI = sessions.some(s => s.mode === 'ai')
      const hasActive = sessions.some(s => (s.status === 'active' || s.status === 'waiting') && s.mode === 'agent')

      if (!hasAI && hasActive && activeTab === 'ai') {
        setActiveTab('active')
      }
    }
  }, [sessions, activeTab])

  // Load the list first so a waiting chat is known to be waiting before it is opened/taken
  useEffect(() => {
    let cancelled = false

    ;(async () => {
      await fetchSessions()
      if (!cancelled) setActiveSession(sessionId || null)
    })()

    return () => { cancelled = true }
  }, [sessionId, fetchSessions, setActiveSession])

  // Show the tab that contains the opened chat
  useEffect(() => {
    if (!activeSession) return
    if (activeSession.status === 'closed' || activeSession.status === 'timeout') setActiveTab('closed')
    else if (activeSession.mode === 'ai') setActiveTab('ai')
    else setActiveTab('active')
  }, [activeSession?.sessionId, activeSession?.status, activeSession?.mode])

  useEffect(() => {
    scrollToBottom()
  }, [messages, activeSessionId])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleSendMessage = async (e) => {
    e.preventDefault()
    if (!messageText.trim() || !activeSession) return

    const result = await sendMessage(activeSession.sessionId, messageText.trim())
    
    if (result.success) {
      setMessageText('')
      inputRef.current?.focus()
    } else {
      toast.error('Failed to send message')
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage(e)
    }
  }

  const handleCloseChat = async () => {
    if (!activeSession) return

    const result = await closeChat(activeSession.sessionId, 'agent_ended')

    if (result.success) {
      toast.success('Chat session closed')
    } else {
      toast.error('Failed to close chat session')
    }
  }

  const sessionMessages = activeSession ? messages[activeSession.sessionId] || [] : []

  // Filter sessions by type
  const aiSessions = sessions.filter(s => s.mode === 'ai' && (s.status === 'active' || s.status === 'waiting'))
  const waitingSessions = sessions.filter(s => s.status === 'waiting' && s.mode === 'agent')
  const activeSessions = sessions.filter(s => s.status === 'active' && s.mode === 'agent')
  const closedSessions = sessions.filter(s => s.status === 'closed' || s.status === 'timeout')

  // Get sessions for current tab
  const getCurrentTabSessions = () => {
    switch (activeTab) {
      case 'ai':
        return aiSessions
      case 'active':
        return [...waitingSessions, ...activeSessions]
      case 'closed':
        return closedSessions
      default:
        return [...waitingSessions, ...activeSessions]
    }
  }

  const currentTabSessions = getCurrentTabSessions()

  return (
    <div className="h-[calc(100dvh-4rem)] flex bg-gray-50 overflow-hidden">
      {/* Sessions Sidebar: full screen on phones until a chat is opened */}
      <div className={`${activeSession ? 'hidden md:flex' : 'flex'} w-full md:w-80 flex-shrink-0 bg-white border-r border-gray-200 flex-col min-h-0`}>
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Chat Sessions</h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  console.log('🔄 Manual refresh triggered')
                  fetchSessions()
                }}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                title="Refresh sessions"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <button
                onClick={() => {
                  console.log('🔍 Socket connection test')
                  console.log('Socket connected:', socketService.isConnected)
                  console.log('Socket ID:', socketService.socket?.id)
                  console.log('Agent sockets on server should show this agent')
                }}
                className="p-2 rounded-lg text-blue-400 hover:text-blue-600 hover:bg-blue-50"
                title="Test socket connection"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 mt-3 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('ai')}
              className={`flex-1 flex items-center justify-center px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                activeTab === 'ai'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Bot className="h-3 w-3 mr-1" />
              AI ({aiSessions.length})
            </button>
            <button
              onClick={() => setActiveTab('active')}
              className={`flex-1 flex items-center justify-center px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                activeTab === 'active'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <CheckCircle className="h-3 w-3 mr-1" />
              Active ({waitingSessions.length + activeSessions.length})
            </button>
            <button
              onClick={() => setActiveTab('closed')}
              className={`flex-1 flex items-center justify-center px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                activeTab === 'closed'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <XCircle className="h-3 w-3 mr-1" />
              Closed ({closedSessions.length})
            </button>
          </div>
        </div>

        {/* Sessions List */}
        <div className="flex-1 overflow-y-auto">
          {currentTabSessions.length > 0 ? (
            <div className="p-4">
              <div className="space-y-2">
                {currentTabSessions.map((session) => {
                  // Determine session appearance based on status and mode
                  const getSessionStyle = () => {
                    if (session.status === 'closed' || session.status === 'timeout') {
                      return {
                        bgColor: 'bg-gray-100',
                        iconBg: 'bg-gray-200',
                        iconColor: 'text-gray-500',
                        badge: session.status === 'timeout' ? 'timeout' : 'closed',
                        badgeClass: 'badge-secondary'
                      }
                    } else if (session.mode === 'ai') {
                      return {
                        bgColor: 'bg-blue-50',
                        iconBg: 'bg-blue-100',
                        iconColor: 'text-blue-600',
                        badge: 'AI',
                        badgeClass: 'badge-info'
                      }
                    } else if (session.status === 'waiting') {
                      return {
                        bgColor: 'bg-yellow-50',
                        iconBg: 'bg-yellow-100',
                        iconColor: 'text-yellow-600',
                        badge: 'waiting',
                        badgeClass: 'badge-warning'
                      }
                    } else {
                      return {
                        bgColor: 'bg-green-50',
                        iconBg: 'bg-green-100',
                        iconColor: 'text-green-600',
                        badge: 'active',
                        badgeClass: 'badge-success'
                      }
                    }
                  }

                  const style = getSessionStyle()

                  return (
                    <motion.div
                      key={session.sessionId}
                      whileHover={{ scale: 1.02 }}
                      onClick={() => navigate(`/chat?session=${session.sessionId}`)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        activeSession?.sessionId === session.sessionId
                          ? 'bg-primary-50 border border-primary-200'
                          : `${style.bgColor} hover:bg-gray-100`
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`h-8 w-8 rounded-full ${style.iconBg} flex items-center justify-center`}>
                            {session.mode === 'ai' ? (
                              <Bot className={`h-4 w-4 ${style.iconColor}`} />
                            ) : (
                              <User className={`h-4 w-4 ${style.iconColor}`} />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {session.mode === 'ai' ? 'AI Chat' : `User ${session.userId.slice(-6)}`}
                            </p>
                            <p className="text-xs text-gray-500">
                              {session.status === 'closed' || session.status === 'timeout'
                                ? formatToIndianDateTime(session?.startedAt)
                                : formatToIndianDateTime(session?.lastActivity)
                              }
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          <span className={`badge ${style.badgeClass}`}>{style.badge}</span>
                          {session.status === 'active' && session.mode !== 'ai' && (
                            <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          ) : (
            /* Empty State */
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">
                  {activeTab === 'ai' && 'No AI chat sessions'}
                  {activeTab === 'active' && 'No active sessions'}
                  {activeTab === 'closed' && 'No closed sessions'}
                </p>
                <p className="text-sm text-gray-400">
                  {activeTab === 'ai' && 'AI conversations will appear here'}
                  {activeTab === 'active' && 'New customer chats will appear here'}
                  {activeTab === 'closed' && 'Completed sessions will appear here'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`${activeSession ? 'flex' : 'hidden md:flex'} flex-1 min-w-0 min-h-0 flex-col`}>
        {activeSession ? (
          <>
            {/* Chat Header */}
            <div className="bg-white border-b border-gray-200 p-3 sm:p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center space-x-2 sm:space-x-4 min-w-0">
                  <button
                    onClick={() => navigate('/chat')}
                    className="md:hidden p-2 -ml-1 rounded-lg text-gray-500 hover:bg-gray-100"
                    aria-label="Back to sessions"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <div className="h-10 w-10 flex-shrink-0 rounded-full bg-primary-100 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base sm:text-lg font-medium text-gray-900 truncate">
                      {activeSession.mode === 'ai' ? 'AI Assistant' : `User ${activeSession.userId.slice(-6)}`}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {activeSession.status === 'active' && 'Online'}
                      {activeSession.status === 'waiting' && 'Waiting for agent'}
                      {activeSession.status === 'closed' && 'Session closed'}
                      {activeSession.status === 'timeout' && 'Session timed out'}
                      {activeSession.mode === 'ai' && activeSession.status === 'active' && 'AI Mode'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
                  <button className="hidden sm:block p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                    <Phone className="h-5 w-5" />
                  </button>
                  <button className="hidden sm:block p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                    <Video className="h-5 w-5" />
                  </button>
                  <button className="hidden sm:block p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                    <Archive className="h-5 w-5" />
                  </button>
                  <button
                    onClick={handleCloseChat}
                    disabled={activeSession.status === 'closed' || activeSession.status === 'timeout'}
                    className={`p-2 rounded-lg transition-colors ${
                      activeSession.status === 'closed' || activeSession.status === 'timeout'
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-red-400 hover:text-red-600 hover:bg-red-50'
                    }`}
                    title={
                      activeSession.status === 'closed' || activeSession.status === 'timeout'
                        ? 'Session already closed'
                        : 'Close chat session'
                    }
                  >
                    <X className="h-5 w-5" />
                  </button>
                  <button className="hidden sm:block p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                    <MoreVertical className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4">
              <AnimatePresence>
                {sessionMessages.map((message, index) => (
                  <motion.div
                    key={message.id || message.messageId || message.tempId || `msg-${index}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className={`flex ${
                      message.sender === 'agent' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div
                      className={`max-w-[85%] sm:max-w-md px-4 py-2 rounded-lg break-words ${
                        message.sender === 'agent'
                          ? 'bg-primary-600 text-white'
                          : message.sender === 'ai'
                          ? 'bg-gray-100 text-gray-900'
                          : 'bg-white border border-gray-200 text-gray-900'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                      <p
                        className={`text-xs mt-1 ${
                          message.sender === 'agent'
                            ? 'text-primary-100'
                            : 'text-gray-500'
                        }`}
                      >
                        {(() => {
                          const timestamp = message.createdAt || message.timestamp;
                          if (!timestamp) return 'Just now';
                          const date = new Date(timestamp);
                          return isNaN(date.getTime()) ? 'Just now' : format(date, 'h:mm a');
                        })()}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
              
              {/* Typing Indicator */}
              {isTyping && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-start"
                >
                  <div className="bg-gray-100 rounded-lg px-4 py-2">
                    <div className="typing-indicator">
                      <div className="typing-dot"></div>
                      <div className="typing-dot"></div>
                      <div className="typing-dot"></div>
                    </div>
                  </div>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            {activeSession.status === 'closed' || activeSession.status === 'timeout' ? (
              <div className="bg-gray-50 border-t border-gray-200 p-4">
                <div className="text-center text-gray-500">
                  <XCircle className="h-6 w-6 mx-auto mb-2" />
                  <p className="text-sm">
                    This chat session has {activeSession.status === 'timeout' ? 'timed out' : 'ended'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {activeSession.endReason && `Reason: ${activeSession.endReason}`}
                  </p>
                </div>
              </div>
            ) : activeSession.mode === 'ai' ? (
              <div className="bg-gray-50 border-t border-gray-200 p-4">
                <div className="text-center text-gray-500">
                  <Bot className="h-6 w-6 mx-auto mb-2" />
                  <p className="text-sm">
                    AI Mode - Monitoring Only
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    The user is chatting with AI. You can observe the conversation but cannot send messages.
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-white border-t border-gray-200 p-3 sm:p-4">
                <form onSubmit={handleSendMessage} className="flex items-end space-x-2 sm:space-x-4">
                  <div className="flex-1">
                    <div className="relative">
                      <textarea
                        ref={inputRef}
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Type your message..."
                        rows={1}
                        className="block w-full resize-none border border-gray-300 rounded-lg px-4 py-3 pr-20 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        style={{ minHeight: '44px', maxHeight: '120px' }}
                      />
                      <div className="absolute right-2 bottom-2 flex items-center space-x-2">
                        <button
                          type="button"
                          className="p-1 rounded text-gray-400 hover:text-gray-600"
                        >
                          <Paperclip className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="p-1 rounded text-gray-400 hover:text-gray-600"
                        >
                          <Smile className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="submit"
                    disabled={!messageText.trim()}
                    className="btn btn-primary p-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="h-4 w-4" />
                  </motion.button>
                </form>
              </div>
            )}
          </>
        ) : (
          /* No Session Selected */
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No session selected
              </h3>
              <p className="text-gray-500">
                Select a chat session from the sidebar to start messaging
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ChatInterface
